import React, { useState, useEffect, useMemo } from "react";
import { intl, fetchData } from "dmsbasereact";
import { ReportLayout } from "./components/ReportLayout";
import { useFormatters } from "../../hooks/useFormater";

export const DispensationsReportPage = () => {
    const formatters = useFormatters();
    const [maxBatches, setMaxBatches] = useState(1);

    useEffect(() => {
        fetchData("/reports/dispensations/filter-options", "GET", true)
            .then((response) => {
                const data = response?.data?.data || response?.data || {};
                const max = data.max_batches || 1;
                setMaxBatches(max);
            })
            .catch((error) => {
                console.error("Failed to fetch filter options:", error);
                setMaxBatches(1);
            });
    }, []);

    /**
     * Header columns conforme sequência esperada em .ai_docs/reports.md:
     * 1. Número do Paciente
     * 2. Nome do paciente
     * 3. Número da Prescrição
     * 4. Status
     * 5. Data da dispensação (registro na plataforma)
     * 6. Farmacêutico dispensador
     * 7. CRF
     * 8. UF do CRF
     * 9. Instituição
     * 10. CNPJ Instituição dispensadora
     * 11. Endereço instituição dispensadora
     * 12. Cidade instituição dispensadora
     * 13. UF Instituição dispensadora
     * 14-N. Lotes dispensados (dinâmico)
     */
    const header = useMemo(() => {
        const baseColumns = [
            // 1. Número do Paciente
            {
                label: intl.get("Reports.Dispensations.PatientNumber").d("Patient No."),
                column_name: "patient_number",
                width: "100px",
            },
            // 2. Nome do paciente
            {
                label: intl.get("Reports.Dispensations.PatientName").d("Patient Name"),
                column_name: "patient_name",
                width: "180px",
            },
            // 3. Número da Prescrição
            {
                label: intl.get("Reports.Dispensations.PrescriptionNumber").d("Prescription No."),
                column_name: "prescription_identifier",
                width: "120px",
            },
            // 4. Status
            {
                label: intl.get("Reports.Dispensations.Status").d("Status"),
                column_name: "status",
                width: "120px",
                function: (value) => intl.get(`DispensingStatus.${value}`).d(value),
            },
            // 5. Data da dispensação (registro na plataforma)
            {
                label: intl.get("Reports.Dispensations.DispensationDate").d("Dispensation Date"),
                column_name: "dispensation_date",
                width: "120px",
            },
            // 5b. Data de entrega (selecionada pelo dispensador)
            {
                label: intl.get("Reports.Dispensations.DeliveryDate").d("Delivery Date"),
                column_name: "delivery_date",
                width: "120px",
            },
            // 6. Farmacêutico dispensador
            {
                label: intl.get("Reports.Dispensations.Pharmacist").d("Pharmacist"),
                column_name: "pharmacist_name",
                width: "180px",
            },
            // 7. CRF
            {
                label: intl.get("Reports.Dispensations.CRF").d("CRF"),
                column_name: "pharmacist_crf",
                width: "100px",
            },
            // 8. UF do CRF
            {
                label: intl.get("Reports.Dispensations.CRFState").d("CRF State"),
                column_name: "pharmacist_uf",
                width: "60px",
            },
            // 9. Instituição
            {
                label: intl.get("Reports.Dispensations.Institution").d("Institution"),
                column_name: "institution_name",
                width: "180px",
            },
            // 10. CNPJ Instituição dispensadora
            {
                label: intl.get("Reports.Dispensations.InstitutionCNPJ").d("Institution CNPJ"),
                column_name: "institution_cnpj",
                width: "150px",
                function: (value) => formatters.formatCNPJ(value),
            },
            // 11. Endereço instituição dispensadora
            {
                label: intl.get("Reports.Dispensations.InstitutionAddress").d("Institution Address"),
                column_name: "institution_address",
                width: "180px",
            },
            // 12. Cidade instituição dispensadora
            {
                label: intl.get("Reports.Dispensations.InstitutionCity").d("Institution City"),
                column_name: "institution_city",
                width: "120px",
            },
            // 13. UF Instituição dispensadora
            {
                label: intl.get("Reports.Dispensations.InstitutionState").d("Institution State"),
                column_name: "institution_state",
                width: "60px",
            },
        ];

        // Dynamic batch columns
        const batchColumns = [];
        for (let i = 1; i <= maxBatches; i++) {
            const suffix = maxBatches > 1 ? ` ${i}` : "";
            batchColumns.push(
                {
                    label: intl.get("Reports.Dispensations.BatchNumber").d("Batch No.") + suffix,
                    column_name: `batch_${i}_number`,
                    width: "100px",
                },
                {
                    label: intl.get("Reports.Dispensations.BatchPresentation").d("Presentation") + suffix,
                    column_name: `batch_${i}_presentation`,
                    width: "120px",
                },
                {
                    label: intl.get("Reports.Dispensations.BoxCount").d("Qty. (box)") + suffix,
                    column_name: `batch_${i}_box_count`,
                    width: "80px",
                },
                {
                    label: intl.get("Reports.Dispensations.BatchCapsules").d("Capsules") + suffix,
                    column_name: `batch_${i}_capsules`,
                    width: "80px",
                },
                {
                    label: intl.get("Reports.Dispensations.BatchFabrication").d("Fabrication") + suffix,
                    column_name: `batch_${i}_fabrication`,
                    width: "110px",
                },
                {
                    label: intl.get("Reports.Dispensations.BatchExpiration").d("Expiration") + suffix,
                    column_name: `batch_${i}_expiration`,
                    width: "110px",
                },
            );
        }

        return [...baseColumns, ...batchColumns];
    }, [maxBatches, formatters]);

    const filterFields = [
        { name: "patient_name", label: intl.get("PatientName").d("Patient Name"), type: "text" },
        { name: "prescription_identifier", label: intl.get("PrescriptionNumber").d("Prescription #"), type: "text" },
        {
            name: "pharmacist_name",
            label: intl.get("Pharmacist").d("Pharmacist"),
            type: "text"
        },
        { name: "pharmacist_crf",
            label: intl.get("CRF").d("CRF"),
            type: "text"
        },
        {
            name: "pharmacist_crf_state",
            label: intl.get("Reports.Dispensations.FilterCRFState").d("CRF State"),
            type: "multiselect",
            optionsKey: "crf_state",
    
        },
        { name: "institution_name",
            label: intl.get("Institution").d("Institution"),
            type: "text"
        },
        { name: "institution_cnpj",
            label: intl.get("CNPJ").d("CNPJ"),
            type: "text"
        },
        { name: "batch_name",
            label: intl.get("Batch").d("Batch"),
            type: "text"
        },
        { name: "status", label: intl.get("Status").d("Status"), type: "multiselect" },
    ];

    return (
        <ReportLayout
            title="Reports.Dispensations.PageTitle"
            titleDefault="Dispensations Report"
            manualSlug="reports-dispensations"
            tableUrl="/reports/dispensations/table"
            filterOptionsUrl="/reports/dispensations/filter-options"
            exportUrl="/reports/dispensations/export"
            header={header}
            filterFields={filterFields}
            dateFilterLabel={intl.get("Reports.DateFilter.DispensationDate").d("Dispensation Date")}
        />
    );
};

export default DispensationsReportPage;
